import React from 'react';
import { motion } from 'framer-motion';
import OfferPack from '../Components/FeatureShowCase/OfferPack';
import { useNavigate } from 'react-router-dom';
import {
    UserCheck,
    Fingerprint,
    Scan,
    Bell,
    Smartphone,
    Shield,
    Calendar,
    CheckCircle,
    Users,
    MessageSquare,
    BookOpen,
    Settings,
    CreditCard,
    Library,
    Bus,
    ChartBar
} from 'lucide-react';

const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
};

const ProductShowcase = () => {

    const navigate = useNavigate();



    const schoolManagement = [
        {
            title: "Academic Management",
            description: "Complete academic lifecycle management",
            icon: <BookOpen className="w-8 h-8 text-yellow-500" />,
            features: [
                "Curriculum planning and management",
                "Assignment and homework tracking",
                "Online examination system",
                "Progress report generation",
                "Parent-teacher communication portal"
            ]
        },
        {
            title: "Administrative Tools",
            description: "Streamline administrative tasks",
            icon: <Settings className="w-8 h-8 text-yellow-500" />,
            features: [
                "Staff management and scheduling",
                "Resource allocation system",
                "Document management",
                "Inventory tracking",
                "Facility management"
            ]
        },
        {
            title: "Financial Management",
            description: "Comprehensive financial tracking",
            icon: <CreditCard className="w-8 h-8 text-yellow-500" />,
            features: [
                "Fee management system",
                "Expense tracking",
                "Payment gateway integration",
                "Salary management",
                "Financial reporting"
            ]
        },
        {
            title: "Library Management",
            description: "Digital library system",
            icon: <Library className="w-8 h-8 text-yellow-500" />,
            features: [
                "Book inventory management",
                "Digital catalog system",
                "Issue and return tracking",
                "Fine calculation",
                "Online resource access"
            ]
        },
        {
            title: "Transportation Management",
            description: "Fleet and route management",
            icon: <Bus className="w-8 h-8 text-yellow-500" />,
            features: [
                "Route optimization",
                "Vehicle tracking",
                "Driver management",
                "Transport fee collection",
                "Real-time location updates"
            ]
        },
        {
            title: "Analytics Dashboard",
            description: "Comprehensive reporting system",
            icon: <ChartBar className="w-8 h-8 text-yellow-500" />,
            features: [
                "Performance analytics",
                "Attendance reports",
                "Financial insights",
                "Student progress tracking",
                "Custom report generation"
            ]
        }
    ];

    const products = [
        {
            title: "RFID-based System",
            description: "Quick and reliable attendance tracking using RFID technology",
            icon: <Scan className="w-12 h-12 text-yellow-500" />,
            details: `Our RFID-based attendance system utilizes advanced radio-frequency identification technology for seamless tracking. Students simply tap their ID cards to record attendance instantly. Features include:
            • Contactless attendance recording
            • Real-time data synchronization
            • Durable cards with long lifespan
            • Multiple scanning points support
            • Backup battery system
            Perfect for large institutions handling high student volumes.`
        },
        {
            title: "RFID + Fingerprint",
            description: "Dual authentication for enhanced security",
            icon: <Fingerprint className="w-12 h-12 text-yellow-500" />,
            details: `Combine the convenience of RFID with the security of biometric verification. This dual authentication system ensures:
            • Foolproof attendance recording
            • Prevention of proxy attendance
            • Fast 2-factor authentication
            • Secure data encryption
            • Multiple fingerprint storage
            Ideal for institutions requiring higher security standards.`
        },
        {
            title: "Face + Fingerprint",
            description: "Advanced biometric authentication system",
            icon: <UserCheck className="w-12 h-12 text-yellow-500" />,
            details: `State-of-the-art facial recognition combined with fingerprint verification. Features include:
            • AI-powered facial recognition
            • Works in various lighting conditions
            • Anti-spoofing technology
            • Quick recognition speed
            • Touchless face scanning option
            Perfect for modern institutions embracing cutting-edge technology.`
        },
        {
            title: "Face + Finger + RFID",
            description: "Ultimate security with triple authentication",
            icon: <Shield className="w-12 h-12 text-yellow-500" />,
            details: `Our most comprehensive solution combining all three authentication methods. Benefits include:
            • Maximum security level
            • Flexible authentication options
            • Advanced anti-spoofing measures
            • Multiple verification modes
            • Perfect for high-security areas
            Recommended for premium institutions and restricted areas.`
        }
    ];

    const features = [
        {
            title: "SMS Notifications",
            description: "Instant updates to parents about attendance",
            icon: <MessageSquare className="w-8 h-8 text-yellow-500" />
        },

    ];

    const addOns = [
        {
            title: "Holiday Notifications",
            description: "Automated holiday announcements",
            icon: <Calendar className="w-8 h-8 text-yellow-500" />
        },
        {
            title: "Leave Management",
            description: "Streamlined leave approval system",
            icon: <CheckCircle className="w-8 h-8 text-yellow-500" />
        },
        {
            title: "Real-time Alerts",
            description: "Instant notifications for important updates",
            icon: <Bell className="w-8 h-8 text-yellow-500" />
        }
    ];

    return (
        <div className="min-h-screen bg-white text-white">
            {/* Hero Section */}
            <motion.section
                className="container  mx-auto px-4 py-20 text-center"
                initial="initial"
                animate="animate"
                variants={fadeInUp}
             >
                <h1 className="text-5xl mt-12 font-bold mb-6">
                    Revolutionize Attendance Management with
                    <span className="text-yellow-500"> Advanced Technology</span>
                </h1>
                <p className="text-xl mb-12 max-w-3xl mx-auto">
                    Transform your institution with cutting-edge attendance solutions using RFID,
                    fingerprint, and facial recognition technologies.
                </p>
                <img
                    src="https://www.lystloc.com/blog/wp-content/uploads/2022/11/ezgif.com-gif-maker-6.webp"
                    alt="Attendance System"
                    className="rounded-lg shadow-xl mx-auto"
                />
            </motion.section>

            {/* Products Grid */}
            <section className="container mx-auto px-4 py-16">
                <h2 className="text-3xl font-bold text-center mb-12">Our Solutions</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {products.map((product, index) => (
                        <motion.div
                            key={index}
                            className="bg-gray-900 p-6 rounded-lg hover:scale-105 transition-transform"
                            whileHover={{ scale: 1.02 }}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <div className="flex items-center mb-4">
                                {product.icon}
                                <div className="ml-4">
                                    <h3 className="text-xl font-bold mb-2">{product.title}</h3>
                                    <p className="text-gray-400">{product.description}</p>
                                </div>
                            </div>
                            <p className="text-gray-300 mt-4 text-sm leading-relaxed whitespace-pre-line">
                                {product.details}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section className="bg-gray-900 py-16">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl font-bold mb-6">Comprehensive School Management System</h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            Transform your educational institution with our all-in-one school management solution.
                            Streamline operations, enhance communication, and improve efficiency across all departments.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {schoolManagement.map((module, index) => (
                            <motion.div
                                key={index}
                                className="bg-white p-6 rounded-lg"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <div className="flex items-center mb-4">
                                    {module.icon}
                                    <div className="ml-4">
                                        <h3 className="text-xl font-bold">{module.title}</h3>
                                        <p className="text-gray-400">{module.description}</p>
                                    </div>
                                </div>
                                <ul className="mt-4 space-y-2">
                                    {module.features.map((feature, fIndex) => (
                                        <li key={fIndex} className="flex items-start">
                                            <CheckCircle className="w-5 h-5 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                                            <span className="text-gray-300">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <OfferPack />

            {/* Features Section */}
            <section className="bg-gray-900 py-16">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Key Features</h2>
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                className="flex flex-col items-center text-center"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.2 }}
                            >
                                <div className="mb-4">{feature.icon}</div>
                                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                <p className="text-gray-400">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Add-ons Section */}
            <section className="container mx-auto px-4 py-16">
                <h2 className="text-3xl font-bold text-center mb-12">Free Add-ons</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {addOns.map((addon, index) => (
                        <motion.div
                            key={index}
                            className="bg-gray-900 p-6 rounded-lg"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.2 }}
                        >
                            <div className="mb-4">{addon.icon}</div>
                            <h3 className="text-xl font-bold mb-2">{addon.title}</h3>
                            <p className="text-gray-400">{addon.description}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="bg-gray-900 py-16">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Why Choose Us</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        <motion.div
                            className="text-center"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                        >
                            <h3 className="text-xl font-bold text-yellow-500 mb-2">Seamless Integration</h3>
                            <p className="text-gray-400">Works perfectly with your existing systems</p>
                        </motion.div>
                        <motion.div
                            className="text-center"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                        >
                            <h3 className="text-xl font-bold text-yellow-500 mb-2">User-Friendly</h3>
                            <p className="text-gray-400">Intuitive interface for all users</p>
                        </motion.div>
                        <motion.div
                            className="text-center"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4 }}
                        >
                            <h3 className="text-xl font-bold text-yellow-500 mb-2">Enhanced Security</h3>
                            <p className="text-gray-400">Advanced protection for your data</p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="container mx-auto px-4 py-20 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Institution?</h2>
                    <button 
                    onClick={() => navigate('/contact')}
                    className="bg-yellow-500 text-black font-bold py-4 px-8 rounded-lg hover:bg-blue-600 transition-colors">
                        
                        Request a Demo
                    </button>
                </motion.div>
            </section>
        </div>
    );
};

export default ProductShowcase;